import { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { Crosshair, X } from "lucide-react";

interface Props {
  /** Initial lat — optional. */
  lat: number | null | undefined;
  lng: number | null | undefined;
  onPick: (lat: number, lng: number) => void;
  onClose: () => void;
}

const DEFAULT_CENTER: [number, number] = [25.276, 55.296]; // Dubai

// Fix default Leaflet icon paths (Vite import quirk).
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/**
 * Modal map picker: click anywhere to drop the marker, drag the marker
 * to fine-tune, then "Use this location" to commit. Used by the Employee
 * editor for manual lat/lng override when the auto-geocoder gets it
 * wrong (e.g. addresses where a foreign-country street name collides
 * with the city extractor).
 */
export default function MapPicker({ lat, lng, onPick, onClose }: Props) {
  const initial: [number, number] =
    lat != null && lng != null ? [lat, lng] : DEFAULT_CENTER;
  const [pos, setPos] = useState<[number, number]>(initial);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-3 sm:p-6 bg-black/70"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl h-[80vh] flex flex-col rounded-2xl bg-[var(--color-surface)] ring-1 ring-[var(--color-border)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div>
            <div className="text-sm uppercase tracking-wide text-slate-400">
              Pick location on map
            </div>
            <div className="text-xs text-slate-500">
              Tap or drag the pin. Coordinates update live.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative flex-1">
          <MapContainer
            center={initial}
            zoom={lat != null ? 11 : 4}
            scrollWheelZoom
            className="h-full w-full"
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <ClickToSet onChange={setPos} />
            <DraggablePin pos={pos} onChange={setPos} />
          </MapContainer>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 inline-flex items-center gap-2">
            <Crosshair size={14} className="text-sky-400" />
            <span className="tabular-nums text-slate-200">
              {pos[0].toFixed(5)}, {pos[1].toFixed(5)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onPick(pos[0], pos[1])}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium"
            >
              Use this location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClickToSet({ onChange }: { onChange: (p: [number, number]) => void }) {
  useMapEvents({
    click(e) {
      onChange([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function DraggablePin({
  pos,
  onChange,
}: {
  pos: [number, number];
  onChange: (p: [number, number]) => void;
}) {
  const map = useMap();
  // Re-center the map when the pin position changes externally (e.g. via
  // the click handler) but keep the user's zoom level.
  useEffect(() => {
    map.panTo(pos, { animate: true });
  }, [pos, map]);

  return (
    <Marker
      position={pos}
      draggable
      eventHandlers={{
        dragend(e) {
          const m = e.target as L.Marker;
          const p = m.getLatLng();
          onChange([p.lat, p.lng]);
        },
      }}
    />
  );
}
